export const DATA_AUTHORITY = Object.freeze({
  CLOUD: 'cloud',
  OPERATIONAL_REPLICA: 'operational-replica',
  DEVICE_LOCAL: 'device-local',
  NOT_APPLICABLE: 'not-applicable',
  MIXED: 'mixed'
});

export const ROUTE_DATA_MODE = Object.freeze({
  CLOUD: 'cloud',
  CLOUD_TRANSITIONAL: 'cloud-transitional',
  OPERATIONAL_REPLICA: 'operational-replica',
  DEVICE_LOCAL: 'device-local'
});

export const OPERATIONAL_BRIDGE_PURPOSE = Object.freeze({
  LEGACY_WRITE: 'legacy-write',
  OPERATIONAL_ROUTE: 'operational-route',
  DEVICE_CAPABILITY: 'device-capability'
});

function unique(values = []) {
  return [...new Set(values.map((value) => String(value || '').trim()).filter(Boolean))];
}

function policy(mode, domains = [], options = {}) {
  const normalizedDomains = unique(domains);
  const readDomains = unique(options.readDomains ?? normalizedDomains);
  const defaultWriteDomains = mode === ROUTE_DATA_MODE.OPERATIONAL_REPLICA
    || mode === ROUTE_DATA_MODE.CLOUD_TRANSITIONAL
    ? normalizedDomains
    : [];
  const writeDomains = unique(options.writeDomains ?? defaultWriteDomains);
  const deviceRoute = mode === ROUTE_DATA_MODE.DEVICE_LOCAL;
  const cloudReadable = mode === ROUTE_DATA_MODE.CLOUD
    || mode === ROUTE_DATA_MODE.CLOUD_TRANSITIONAL;
  const cloudWritable = mode === ROUTE_DATA_MODE.CLOUD;

  return Object.freeze({
    mode,
    domains: Object.freeze(normalizedDomains),
    readDomains: Object.freeze(readDomains),
    writeDomains: Object.freeze(writeDomains),
    defaultReadAuthority: deviceRoute
      ? DATA_AUTHORITY.DEVICE_LOCAL
      : (cloudReadable ? DATA_AUTHORITY.CLOUD : DATA_AUTHORITY.OPERATIONAL_REPLICA),
    defaultWriteAuthority: deviceRoute
      ? DATA_AUTHORITY.DEVICE_LOCAL
      : (cloudWritable ? DATA_AUTHORITY.CLOUD : DATA_AUTHORITY.OPERATIONAL_REPLICA),
    cloudReadCapable: options.cloudReadCapable ?? cloudReadable,
    cloudWriteCapable: options.cloudWriteCapable ?? cloudWritable,
    requiresFullCloudAuthority: options.requiresFullCloudAuthority === true
  });
}

// Contrato fechado das rotas autenticadas. A inclusao de uma nova pagina sem
// declarar sua fonte aqui deve falhar nos testes do shell, em vez de chegar em
// producao consultando silenciosamente uma base vazia.
export const AUTHENTICATED_ROUTE_DATA_POLICIES = Object.freeze({
  '/': policy(
    ROUTE_DATA_MODE.CLOUD,
    [
      'orders',
      'order_items',
      'customers',
      'products',
      'suppliers',
      'tables',
      'dashboard_banners'
    ]
  ),
  '/inteligencia-publico': policy(ROUTE_DATA_MODE.CLOUD, ['orders', 'order_items', 'customers', 'products']),
  '/pdv': policy(
    ROUTE_DATA_MODE.OPERATIONAL_REPLICA,
    [
      'customers',
      'products',
      'tables',
      'payment_methods',
      'cash_sessions',
      'product_variants',
      'product_modifiers',
      'orders',
      'order_items',
      'inventory_movements',
      'payments',
      'receivables'
    ],
    {
      readDomains: [
        'customers',
        'products',
        'tables',
        'payment_methods',
        'cash_sessions',
        'product_variants',
        'product_modifiers'
      ],
      writeDomains: [
        'orders',
        'order_items',
        'inventory_movements',
        'payments',
        'receivables'
      ],
      cloudReadCapable: true,
      cloudWriteCapable: true,
      requiresFullCloudAuthority: true
    }
  ),
  '/caixa': policy(
    ROUTE_DATA_MODE.OPERATIONAL_REPLICA,
    ['cash_sessions', 'cash_movements', 'payments', 'cash_reconciliations'],
    {
      cloudReadCapable: true,
      cloudWriteCapable: true,
      requiresFullCloudAuthority: true
    }
  ),
  '/comandas-mobile': policy(
    ROUTE_DATA_MODE.OPERATIONAL_REPLICA,
    ['customers', 'products', 'tables', 'orders', 'order_items', 'product_measures'],
    {
      readDomains: ['products', 'tables', 'orders'],
      writeDomains: ['orders'],
      cloudReadCapable: true,
      cloudWriteCapable: true,
      requiresFullCloudAuthority: true
    }
  ),
  '/entregas': policy(
    ROUTE_DATA_MODE.OPERATIONAL_REPLICA,
    ['customers', 'orders', 'deliveries'],
    {
      readDomains: ['orders', 'deliveries'],
      writeDomains: ['deliveries'],
      cloudReadCapable: true,
      cloudWriteCapable: true,
      requiresFullCloudAuthority: true
    }
  ),
  '/pedidos': policy(
    ROUTE_DATA_MODE.CLOUD,
    ['orders', 'order_items', 'payments', 'returns', 'order_logs'],
    {
      writeDomains: ['order_logs', 'returns']
    }
  ),
  '/clientes': policy(
    ROUTE_DATA_MODE.CLOUD,
    ['customers'],
    { writeDomains: ['customers'] }
  ),
  '/fornecedores': policy(
    ROUTE_DATA_MODE.CLOUD,
    ['suppliers'],
    { writeDomains: ['suppliers'] }
  ),
  '/entregadores': policy(
    ROUTE_DATA_MODE.OPERATIONAL_REPLICA,
    ['deliveries', 'employees'],
    {
      readDomains: ['deliveries'],
      writeDomains: ['deliveries'],
      cloudReadCapable: true,
      cloudWriteCapable: true,
      requiresFullCloudAuthority: true
    }
  ),
  '/produtos': policy(
    ROUTE_DATA_MODE.CLOUD,
    ['products', 'categories', 'inventory_movements', 'product_variants'],
    { writeDomains: ['products'] }
  ),
  '/tabelas-preco': policy(
    ROUTE_DATA_MODE.OPERATIONAL_REPLICA,
    ['customers', 'products', 'tables', 'product_variants'],
    {
      readDomains: ['customers', 'products', 'tables', 'product_variants'],
      writeDomains: ['tables'],
      cloudReadCapable: true,
      cloudWriteCapable: true,
      requiresFullCloudAuthority: true
    }
  ),
  '/inventario': policy(
    ROUTE_DATA_MODE.OPERATIONAL_REPLICA,
    ['products', 'inventory_movements', 'stock_counts'],
    {
      writeDomains: ['inventory_movements', 'stock_counts'],
      cloudReadCapable: true,
      cloudWriteCapable: true,
      requiresFullCloudAuthority: true
    }
  ),
  '/catalogo-operacional': policy(
    ROUTE_DATA_MODE.OPERATIONAL_REPLICA,
    [
      'products',
      'product_measures',
      'product_variants',
      'product_modifiers',
      'recipes',
      'recipe_items'
    ],
    {
      cloudReadCapable: true,
      cloudWriteCapable: true,
      requiresFullCloudAuthority: true
    }
  ),
  '/categorias-produtos': policy(
    ROUTE_DATA_MODE.CLOUD,
    ['categories'],
    { writeDomains: ['categories'] }
  ),
  '/tabela-frete': policy(
    ROUTE_DATA_MODE.OPERATIONAL_REPLICA,
    ['deliveries'],
    {
      cloudReadCapable: true,
      cloudWriteCapable: true,
      requiresFullCloudAuthority: true
    }
  ),
  '/mesas': policy(
    ROUTE_DATA_MODE.OPERATIONAL_REPLICA,
    ['tables'],
    {
      cloudReadCapable: true,
      cloudWriteCapable: true,
      requiresFullCloudAuthority: true
    }
  ),
  '/ordens-compra': policy(
    ROUTE_DATA_MODE.OPERATIONAL_REPLICA,
    ['suppliers', 'products', 'purchase_orders', 'purchase_items', 'purchase_invoices'],
    {
      cloudReadCapable: true,
      cloudWriteCapable: true,
      requiresFullCloudAuthority: true
    }
  ),
  '/financeiro': policy(
    ROUTE_DATA_MODE.OPERATIONAL_REPLICA,
    [
      'bank_accounts',
      'bank_statement_imports',
      'bank_statement_transactions',
      'payment_methods',
      'suppliers',
      'customers',
      'finance_entries',
      'receivables'
    ],
    {
      writeDomains: ['finance_entries', 'receivables'],
      cloudReadCapable: true,
      cloudWriteCapable: true,
      requiresFullCloudAuthority: true
    }
  ),
  '/financeiro-organizador': policy(
    ROUTE_DATA_MODE.CLOUD,
    ['finance_entries'],
    {
      writeDomains: ['finance_entries'],
      cloudReadCapable: true,
      cloudWriteCapable: true,
      requiresFullCloudAuthority: true
    }
  ),
  '/dre': policy(
    ROUTE_DATA_MODE.CLOUD,
    ['orders', 'finance_entries', 'receivables', 'products'],
    {
      cloudReadCapable: true,
      cloudWriteCapable: false,
      requiresFullCloudAuthority: true
    }
  ),
  '/financeiro-configuracoes': policy(
    ROUTE_DATA_MODE.OPERATIONAL_REPLICA,
    ['bank_accounts', 'payment_methods', 'finance_entries'],
    {
      cloudReadCapable: true,
      cloudWriteCapable: true,
      requiresFullCloudAuthority: true
    }
  ),
  '/crediario': policy(
    ROUTE_DATA_MODE.OPERATIONAL_REPLICA,
    ['customers', 'orders', 'receivables', 'credit_vouchers'],
    {
      cloudReadCapable: true,
      cloudWriteCapable: true,
      requiresFullCloudAuthority: true
    }
  ),
  '/colaboradores': policy(
    ROUTE_DATA_MODE.OPERATIONAL_REPLICA,
    ['employees'],
    {
      cloudReadCapable: true,
      cloudWriteCapable: true,
      requiresFullCloudAuthority: true
    }
  ),
  '/ponto': policy(
    ROUTE_DATA_MODE.OPERATIONAL_REPLICA,
    ['employees', 'time_entries', 'people_requests', 'time_integrations'],
    {
      cloudReadCapable: true,
      cloudWriteCapable: true,
      requiresFullCloudAuthority: true
    }
  ),
  '/agenda-comissoes': policy(
    ROUTE_DATA_MODE.OPERATIONAL_REPLICA,
    [
      'customers',
      'products',
      'employees',
      'appointments',
      'commission_rules',
      'commission_entries',
      'commission_settlements'
    ],
    {
      cloudReadCapable: true,
      cloudWriteCapable: true,
      requiresFullCloudAuthority: true
    }
  ),
  '/ponto-kiosk': policy(
    ROUTE_DATA_MODE.OPERATIONAL_REPLICA,
    ['employees', 'time_entries'],
    {
      cloudReadCapable: true,
      cloudWriteCapable: true,
      requiresFullCloudAuthority: true
    }
  ),
  '/base-conhecimento': policy(
    ROUTE_DATA_MODE.OPERATIONAL_REPLICA,
    ['knowledge_base'],
    {
      cloudReadCapable: true,
      cloudWriteCapable: true,
      requiresFullCloudAuthority: true
    }
  ),
  '/relatorios': policy(
    ROUTE_DATA_MODE.OPERATIONAL_REPLICA,
    [
      'reporting',
      'customers',
      'products',
      'categories',
      'suppliers',
      'bank_accounts',
      'payment_methods',
      'tables',
      'orders',
      'order_items',
      'finance_entries',
      'receivables',
      'purchase_orders',
      'purchase_invoices',
      'returns',
      'employees',
      'time_entries'
    ],
    {
      writeDomains: [],
      cloudReadCapable: true,
      cloudWriteCapable: true,
      requiresFullCloudAuthority: true
    }
  ),
  '/consultas': policy(
    ROUTE_DATA_MODE.OPERATIONAL_REPLICA,
    [
      'reporting',
      'saved_queries',
      'customers',
      'products',
      'categories',
      'suppliers',
      'bank_accounts',
      'payment_methods',
      'tables',
      'orders',
      'order_items',
      'finance_entries',
      'receivables',
      'purchase_orders',
      'purchase_items',
      'employees',
      'time_entries'
    ],
    {
      writeDomains: ['saved_queries'],
      cloudReadCapable: true,
      cloudWriteCapable: true,
      requiresFullCloudAuthority: true
    }
  ),
  '/faq-suporte': policy(
    ROUTE_DATA_MODE.CLOUD,
    ['support_tickets'],
    { writeDomains: ['support_tickets'] }
  ),
  '/configuracoes': policy(
    ROUTE_DATA_MODE.CLOUD,
    ['tenant_settings', 'tenant_branding', 'cash_automation'],
    { writeDomains: ['tenant_settings', 'tenant_branding', 'cash_automation'] }
  ),
  '/fiscal': policy(ROUTE_DATA_MODE.CLOUD, [
    'customers',
    'orders',
    'fiscal_documents',
    'fiscal_settings',
    'fiscal_credentials',
    'fiscal_certificates'
  ]),
  '/espaco-contador': policy(
    ROUTE_DATA_MODE.OPERATIONAL_REPLICA,
    [
      'accounting_audit',
      'customers',
      'products',
      'suppliers',
      'bank_accounts',
      'payment_methods',
      'orders',
      'order_items',
      'order_logs',
      'payments',
      'returns',
      'fiscal_documents',
      'finance_entries',
      'receivables'
    ],
    {
      writeDomains: ['accounting_audit'],
      cloudReadCapable: true,
      cloudWriteCapable: true,
      requiresFullCloudAuthority: true
    }
  ),
  '/nfe-mg': policy(ROUTE_DATA_MODE.CLOUD, [
    'customers',
    'orders',
    'fiscal_documents',
    'fiscal_settings',
    'fiscal_credentials',
    'fiscal_certificates'
  ]),
  '/nfse-itabira': policy(ROUTE_DATA_MODE.CLOUD, [
    'customers',
    'orders',
    'fiscal_documents',
    'fiscal_settings',
    'fiscal_credentials',
    'fiscal_certificates'
  ]),
  '/dispositivos': policy(ROUTE_DATA_MODE.DEVICE_LOCAL),
  '/conecta-clube': policy(
    ROUTE_DATA_MODE.CLOUD,
    ['customers', 'products', 'conecta_integration'],
    { writeDomains: ['conecta_integration'] }
  ),
  '/autorizar-conecta': policy(
    ROUTE_DATA_MODE.CLOUD,
    ['conecta_integration'],
    { writeDomains: ['conecta_integration'] }
  ),
  '/wbuy': policy(
    ROUTE_DATA_MODE.CLOUD,
    ['wbuy_integration'],
    { writeDomains: ['wbuy_integration'] }
  ),
  '/correios': policy(
    ROUTE_DATA_MODE.CLOUD,
    ['customers', 'orders', 'correios_integration'],
    { writeDomains: ['correios_integration'] }
  ),
  '/pagarme': policy(
    ROUTE_DATA_MODE.CLOUD,
    ['orders', 'payments', 'pagarme_integration', 'pagarme_reconciliation'],
    { writeDomains: ['pagarme_integration', 'pagarme_reconciliation'] }
  ),
  '/central-novidades': policy(
    ROUTE_DATA_MODE.CLOUD,
    ['dashboard_banners'],
    { writeDomains: ['dashboard_banners'] }
  ),
  '/banners-parceiros': policy(
    ROUTE_DATA_MODE.CLOUD,
    ['dashboard_banners'],
    { writeDomains: ['dashboard_banners'] }
  ),
  '/instalador-local': policy(ROUTE_DATA_MODE.DEVICE_LOCAL)
});

function persistenceMode(user) {
  return String(
    user?.tenant?.persistenceMode
      || user?.tenant?.persistence_mode
      || user?.persistence_mode
      || user?.persistenceMode
      || 'cloud_authoritative'
  ).trim().toLowerCase();
}

function tenantDataAuthority(user) {
  const value = user?.tenant?.dataAuthority
    || user?.tenant?.data_authority
    || user?.tenant_data_authority
    || {};
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function isCentralSession(user) {
  return Boolean(
    user?.session_mode === 'central'
      || (user?.tenant?.id && user?.user?.id)
  );
}

function activeOverride(map, domain) {
  const entry = map?.[domain];
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return null;
  const rolloutStatus = String(
    entry.rolloutStatus || entry.rollout_status || 'legacy'
  ).trim().toLowerCase();
  return ['canary', 'complete'].includes(rolloutStatus) ? entry : null;
}

function domainAuthority({
  map,
  domain,
  field,
  fallback,
  cloudCapable
}) {
  const override = activeOverride(map, domain);
  if (!override) return fallback;
  const snakeField = field === 'readAuthority'
    ? 'read_authority'
    : (field === 'writeAuthority' ? 'write_authority' : field);
  const value = String(override[field] ?? override[snakeField] ?? '').trim().toLowerCase();
  if (value === DATA_AUTHORITY.CLOUD) {
    return cloudCapable ? DATA_AUTHORITY.CLOUD : fallback;
  }
  return [
    DATA_AUTHORITY.OPERATIONAL_REPLICA,
    DATA_AUTHORITY.DEVICE_LOCAL
  ].includes(value) ? value : fallback;
}

function summarizeAuthorities(authorities = []) {
  if (!authorities.length) return DATA_AUTHORITY.NOT_APPLICABLE;
  const values = unique(authorities);
  return values.length === 1 ? values[0] : DATA_AUTHORITY.MIXED;
}

function resolvedMode(base, readAuthority, writeAuthority) {
  if (base.mode === ROUTE_DATA_MODE.DEVICE_LOCAL
      || readAuthority === DATA_AUTHORITY.DEVICE_LOCAL
      || writeAuthority === DATA_AUTHORITY.DEVICE_LOCAL) {
    return ROUTE_DATA_MODE.DEVICE_LOCAL;
  }
  const readIsCloud = [
    DATA_AUTHORITY.CLOUD,
    DATA_AUTHORITY.NOT_APPLICABLE
  ].includes(readAuthority);
  const writeIsCloud = [
    DATA_AUTHORITY.CLOUD,
    DATA_AUTHORITY.NOT_APPLICABLE
  ].includes(writeAuthority);
  if (base.requiresFullCloudAuthority && (!readIsCloud || !writeIsCloud)) {
    return ROUTE_DATA_MODE.OPERATIONAL_REPLICA;
  }
  if (readIsCloud && writeIsCloud) return ROUTE_DATA_MODE.CLOUD;
  if (readIsCloud) return ROUTE_DATA_MODE.CLOUD_TRANSITIONAL;
  return ROUTE_DATA_MODE.OPERATIONAL_REPLICA;
}

export function routeDataPolicy(path) {
  return AUTHENTICATED_ROUTE_DATA_POLICIES[String(path || '')] || null;
}

export function resolveRouteDataPolicy(user, path) {
  const base = routeDataPolicy(path);
  if (!base) return null;
  if (persistenceMode(user) === 'local') return base;

  const map = tenantDataAuthority(user);
  const readAuthorities = base.readDomains.map((domain) => domainAuthority({
    map,
    domain,
    field: 'readAuthority',
    fallback: base.defaultReadAuthority,
    cloudCapable: base.cloudReadCapable
  }));
  const writeAuthorities = base.writeDomains.map((domain) => domainAuthority({
    map,
    domain,
    field: 'writeAuthority',
    fallback: base.defaultWriteAuthority,
    cloudCapable: base.cloudWriteCapable
  }));
  const readAuthority = summarizeAuthorities(readAuthorities);
  const writeAuthority = summarizeAuthorities(writeAuthorities);

  return Object.freeze({
    ...base,
    mode: resolvedMode(base, readAuthority, writeAuthority),
    declaredMode: base.mode,
    readAuthority,
    writeAuthority
  });
}

export function routeUsesCloudDirectly(user, path) {
  const resolved = resolveRouteDataPolicy(user, path);
  return Boolean(
    isCentralSession(user)
    && persistenceMode(user) !== 'local'
    && [
      ROUTE_DATA_MODE.CLOUD,
      ROUTE_DATA_MODE.CLOUD_TRANSITIONAL
    ].includes(resolved?.mode)
  );
}

export function routeCanRenderWithoutPlatformBridge(
  user,
  path,
  isElectronRuntime = false
) {
  if (isElectronRuntime || !user || !isCentralSession(user)) return false;
  if (persistenceMode(user) === 'local') return false;
  const mode = resolveRouteDataPolicy(user, path)?.mode;
  return [
    ROUTE_DATA_MODE.CLOUD,
    ROUTE_DATA_MODE.CLOUD_TRANSITIONAL,
    ROUTE_DATA_MODE.DEVICE_LOCAL
  ].includes(mode);
}

export function routeNeedsPlatformBridge(user, path, isElectronRuntime = false) {
  if (isElectronRuntime) return true;
  const resolved = resolveRouteDataPolicy(user, path);
  if (!resolved || !user) return false;
  if (!isCentralSession(user)) return true;
  if (persistenceMode(user) === 'local') return true;
  return resolved.mode === ROUTE_DATA_MODE.OPERATIONAL_REPLICA;
}

export function routeNeedsInitialCentralSync(user, path, isElectronRuntime = false) {
  if (isElectronRuntime || !isCentralSession(user)) return false;
  if (persistenceMode(user) === 'local') return false;
  return resolveRouteDataPolicy(user, path)?.mode === ROUTE_DATA_MODE.OPERATIONAL_REPLICA;
}

export function routeAllowsOperationalBridge(
  user,
  path,
  purpose,
  isElectronRuntime = false
) {
  if (isElectronRuntime || persistenceMode(user) === 'local') return true;
  if (!user || !isCentralSession(user)) return true;
  const resolved = resolveRouteDataPolicy(user, path);
  if (!resolved) return false;
  if (resolved.mode === ROUTE_DATA_MODE.CLOUD) return false;
  if (resolved.mode === ROUTE_DATA_MODE.CLOUD_TRANSITIONAL) {
    return purpose === OPERATIONAL_BRIDGE_PURPOSE.LEGACY_WRITE;
  }
  return true;
}

export function routeDataTelemetry(user, path) {
  const resolved = resolveRouteDataPolicy(user, path);
  if (!resolved) return null;
  return Object.freeze({
    route: String(path || ''),
    mode: resolved.mode,
    declaredMode: resolved.declaredMode || resolved.mode,
    readAuthority: resolved.readAuthority || resolved.defaultReadAuthority,
    writeAuthority: resolved.writeAuthority || (
      resolved.writeDomains.length
        ? resolved.defaultWriteAuthority
        : DATA_AUTHORITY.NOT_APPLICABLE
    ),
    domains: resolved.domains.join(',')
  });
}
